<template>
  <Modal
    class="tw-translate-y-[-50%] add-domain-modal"
    :max-width="DEFAULT_MAX_WIDTH_MODAL"
    :visible="showRef"
    :value="form"
    @close="handleCloseModal"
  >
    <ModalHeader
      :title="getUiText('app-settings-domain-add-modal-title-1')"
      @close="handleCloseModal"
    />
    <div class="tw-p-10 modal-add-domain">
      <div
        :class="`tw-text-[25px] tw-text-[#211F58] tw-font-[Poppins] tw-font-[600] tw-mb-[15px] tw-leading-[145%]`"
      >
        {{ getComponentUiText("app-settings-domain-add-new-modal-title-1") }}
      </div>
      <div
        class="tw-text-[15px] tw-text-[#231F20] tw-font-[400] tw-leading-[145%] tw-mb-[25px]"
      >
        {{ getComponentUiText("app-settings-domain-add-modal-description-1") }}
      </div>
      <BaseForm :show="showRef" :enter-event="true" @on:submit="addDomain">
        <InputField
          v-model:value="form.domain"
          type="input"
          data-cy="add-domain-modal-input"
          :placeholder="
            getUiText('app-settings-domain-add-modal-input-prompt-1')
          "
          :class-style="customClassInputEmail"
          class="autoFocus"
          @update:value="updateValueInputFiled"
          @on:blur="onFocus(false)"
          @on:focus="onFocus(true)"
        >
          <template #icon>
            <img
              v-if="form.domain !== ''"
              class="tw-w-3 tw-h-3 icon-input-clear tw-cursor-pointer"
              src="@/assets/img/close-grey.svg"
              alt="close"
              @click="clearData"
            />
          </template>
        </InputField>
        <p
          v-if="
            v$.domain.$errors.length > 0 &&
            (!focus ||
              (focus &&
                v$.domain.$errors.findIndex(
                  (error) => error.$validator !== 'domain',
                ) >= 0))
          "
          class="tw-text-[#F7553C] tw-text-[11px] tw-leading-none tw-my-[10px]"
        >
          <span v-if="focus">{{ getErrorMessage(v$.domain.$errors) }}</span>
          <span v-if="!focus">{{ v$.domain.$errors?.[0]?.$message }}</span>
        </p>
        <div class="tw-flex tw-justify-end tw-mt-[25px]">
          <b-button
            pill
            variant="primary"
            data-cy="add-domain-modal-button"
            :class="`button-setting-add-domain tw-bg-[#387DFF] tw-py-[11px] ${
              loading ? 'tw-relative' : ''
            } tw-text-[15px] tw-font-medium tw-leading-none`"
            type="submit"
          >
            <span :class="`${loading ? 'tw-text-transparent' : ''}`"
              >{{ getUiText("app-settings-domain-add-modal-add-button-1") }}
            </span>
            <div
              :class="`tw-w-[39px] tw-h-[39px] tw-flex tw-items-center tw-justify-center tw-absolute tw-top-[-1] ${
                loading ? '' : 'tw-hidden'
              }`"
            >
              <!-- eslint-disable vue/no-unused-refs -->
              <LottieAnimation
                ref="anim"
                :animation-data="ProgressAnimation"
                :auto-play="true"
                :loop="true"
              />
            </div>
          </b-button>
        </div>
      </BaseForm>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import store from "../../store";
import utilities, {
  IsSpecialCharDomainValid,
  getComponentUiText,
} from "../../utils/utilities";
import { Ref, computed, reactive, ref, watch } from "vue";
import ProgressAnimation from "../../assets/animation/progress_dark.json";
import { LottieAnimation } from "lottie-web-vue";

import Modal from "../common/modal.vue";
import ModalHeader from "../common/modalHeader.vue";
import InputField from "../common/inputField1.vue";
import { postAddOrgDomain } from "@/services/org";
import { tlDomain } from "@/utils/validationRules";
import { helpers, required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";

import commonServices from "@/services/common/commonServices";
import BaseForm from "../common/form/baseForm.vue";
import { showErrorAPI } from "@/services/apiRoutes";
import { Org } from "@/services/apiRoutes/routes";

const DEFAULT_MAX_WIDTH_MODAL = 65;
const cssFieldEmail =
  "tw-gap-2.5 tw-border tw-text-[#231F20] tw-text-[15px] tw-leading-none tw-pl-[25px] tw-pr-[45px] tw-py-[20px] tw-rounded-md tw-border-solid tw-border-[#EFF0F1] tw-bg-[#F7F7F8]";
const CUSTOM_CLASS_INPUT_INVALID = "input-invalid-style-1";
const customClassInputEmail = ref(cssFieldEmail);
const initialForm = { domain: "" };
const form = reactive(initialForm);
const focus = ref(false);
const rules = {
  domain: {
    required: helpers.withMessage(
      () => getUiText("app-common-form-value-required-error-1"),
      required,
    ),
    specialChar: helpers.withMessage(
      () =>
        getComponentUiText(
          "app-base-input-email-invalid-special-domain-characters-1",
        ),
      IsSpecialCharDomainValid,
    ),
    domain: helpers.withMessage(
      () =>
        getUiText(
          "app-settings-domain-add-modal-domain-invalid-error-message-1",
        ),
      tlDomain,
    ),
  },
};

const props = defineProps({
  show: {
    type: Boolean,
    required: false,
  },
});
const emit = defineEmits(["update:show", "add:domain:success"]);
const v$ = useVuelidate(rules, form);
const loading = ref(false);
const showRef: Ref<boolean> = ref(props.show);
const isDisableAddDomain: Ref<boolean> = ref(true);
const errorAddDomain: Ref<string> = ref("");
const commonService = new commonServices();
let isAddingDomain = false;
watch(
  () => props.show,
  (value) => {
    showRef.value = value;
    if (props.show === true) {
      form.domain = "";
      isDisableAddDomain.value = true;
      customClassInputEmail.value = cssFieldEmail;
    }
  },
);

const defaultUiText = computed(() => store.state.LanguagePicker.defaultUiText);
const languageSelectedText = computed(
  () => store.state.LanguagePicker.languageSelectedText,
);

const getUiText = (id: string, isArray = false) => {
  return utilities.getUiText(
    id,
    languageSelectedText.value,
    defaultUiText.value,
    isArray,
  );
};

const onFocus = (isFocus) => {
  focus.value = isFocus;
  handleUpdateCssInput();
};

const getErrorMessage = (errors: any) => {
  const idxMessage = errors.findIndex((error) => error.$validator !== "domain");
  return errors[idxMessage].$message;
};

const handleUpdateCssInput = () => {
  if (
    v$.value.domain.$errors.length === 0 ||
    (focus.value &&
      v$.value.domain.$errors.length === 1 &&
      v$.value.domain.$errors[0].$validator === "domain")
  ) {
    isDisableAddDomain.value = false;
    customClassInputEmail.value = cssFieldEmail;
  } else {
    isDisableAddDomain.value = true;
    customClassInputEmail.value = `${cssFieldEmail} ${CUSTOM_CLASS_INPUT_INVALID}`;
  }
};

const updateValueInputFiled = (value) => {
  if (value.trim() === "") v$.value.domain.$reset();
  else {
    v$.value.domain.$touch();
    v$.value.domain.$model = value;
  }

  handleUpdateCssInput();
};

const addDomain = async () => {
  if (isAddingDomain) return;
  isAddingDomain = true;
  await v$.value.$validate();
  if (v$.value.$invalid) {
    handleUpdateCssInput();
    isAddingDomain = false;
    return;
  }

  try {
    commonService.setCursorLoading();
    loading.value = true;
    const res = await postAddOrgDomain(form.domain);
    handleCloseModal();
    if (res && res.data && res.data.data)
      emit("add:domain:success", {
        id: res.data.data.domainId,
        ...res.data.data,
      });
  } catch (error: any) {
    if (error && error.data && error.data.error) {
      errorAddDomain.value = error.data.error.message || "";
      if (error && error.data) {
        showErrorAPI(error, Org.domain);
      }

      isDisableAddDomain.value = true;
      customClassInputEmail.value = `${cssFieldEmail} ${CUSTOM_CLASS_INPUT_INVALID}`;
    }
  } finally {
    loading.value = false;
    isAddingDomain = false;
    commonService.setCursorDefault();
  }
};

const handleCloseModal = () => {
  loading.value = false;
  clearData();
  showRef.value = false;
  emit("update:show", showRef.value);
};

const clearData = () => {
  form.domain = "";
  isDisableAddDomain.value = true;
  customClassInputEmail.value = cssFieldEmail;
  v$.value.domain.$model = "";
  v$.value.domain.$reset();
};
</script>

<style scoped lang="scss">
.icon-input-clear {
  position: absolute;
  right: 67px;
  height: 65px;
}

.modal-add-domain input::placeholder {
  font-size: 15px !important;
}

.button-setting-add-domain {
  background-color: #387dff;
  border-color: #387dff;
}

@media (max-width: 991px) {
  .icon-input-clear {
    right: 65px !important;
  }
}
</style>
